/* eslint-disable max-lines */
import { fetchUser, fetchUserProfileAggs } from 'actions';
import LoadingIndicator from 'components/LoadingIndicator';
import useClickOutside from 'components/utils/useClickOutside';
import { CLOSE_ICON } from 'constants/envelopeIcons';
import { startCase } from 'lodash';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { usePopper } from 'react-popper';
import { useDispatch } from 'react-redux';
import { getCustomerDomain } from 'selectors/auth';
import { getFetchUserProfileAggsLoading, getUser } from 'selectors/users';
import { useSelector } from 'store';
import { buildQSFromQSObj } from 'utils/urls';

type ComponentProps = {
  userId: string;
  setProfileOpen: React.Dispatch<React.SetStateAction<boolean>>;
};

const UserProfile: React.FC<ComponentProps> = ({ userId, setProfileOpen }) => {
  const dispatch = useDispatch();

  const user = useSelector(getUser(userId));
  const aggsLoading = useSelector(getFetchUserProfileAggsLoading);
  const domain = useSelector(getCustomerDomain);

  const [popperRef, setPopperRef] = useState<HTMLDivElement | null>(null);
  const ref = useRef<HTMLDivElement>(null);
  const cardRef = useRef<HTMLDivElement>(null);

  const { styles, attributes } = usePopper(ref.current, popperRef, {
    placement: 'bottom-start',
  });

  // click outside functionality
  const handleClickOutside = useCallback(() => setProfileOpen(false), [setProfileOpen]);
  useClickOutside([ref, cardRef], handleClickOutside);

  useEffect(() => {
    if (!user) {
      dispatch(fetchUser({ userId }));
    }
  }, [dispatch, user, userId]);

  useEffect(() => {
    if (user && !user.topContacts) {
      dispatch(fetchUserProfileAggs({ userId }));
    }
  }, [dispatch, user, userId]);

  const onClose = (): void => {
    setProfileOpen(false);
  };

  const isMonitored =
    user?.insight_active_chat ||
    user?.insight_active_email ||
    user?.prevent_active_chat ||
    user?.prevent_active_email;

  const renderProfile = (): React.ReactPortal | null => {
    const portal = document.getElementById('dropdown-portal');

    const cardElement = (
      <div
        className="relative min-w-80 p-2 bg-white rounded-lg"
        ref={(node): void => {
          setPopperRef(node);
        }}
        style={{ ...styles.popper, boxShadow: '0 4px 10px 0 rgba(0, 0, 0, 0.50)' }}
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...attributes.popper}
      >
        <div ref={cardRef}>
          {!user || aggsLoading ? (
            <div className="flex justify-center items-center w-78 h-56">
              <LoadingIndicator size="10" />
            </div>
          ) : (
            <div className="flex flex-col text-small gap-2">
              <div className="flex flex-col">
                <div className="mb-1 flex flex-row justify-between items-center">
                  <span className="text-subtitle">{startCase(user?.name || '')}</span>
                  <button type="button" className="focus:outline-none" onClick={onClose}>
                    {CLOSE_ICON}
                  </button>
                </div>
                <div>
                  <span className="text-litlingo-info">{user?.email}</span>
                </div>
                <div className="flex flex-row justify-between items-center">
                  <span>{user?.teams?.[0]?.name}</span>
                  <span
                    className={`px-1 pt-1 rounded ${
                      isMonitored ? 'bg-litlingo-info-light' : 'bg-litlingo-gray-1'
                    }`}
                  >
                    {isMonitored ? 'Monitored' : 'Unmonitored'}
                  </span>
                </div>
              </div>
              <div className="border-b border-litlingo-gray-2" />
              <div className="flex flex-col gap-2">
                <div className="flex flex-col gap-1">
                  <span>In retention</span>
                  <ul className="flex flex-col gap-1">
                    <li className="list-disc list-inside pl-2 underline cursor-pointer">
                      <a
                        href={`${
                          window.location.origin
                        }/${domain}/communication-envelopes?${buildQSFromQSObj({
                          envelopes__filters_search: user?.higherRiskFilter || '',
                        })}`}
                        className="text-small underline cursor-pointer"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {user.higherRiskCount || 0} Higher risk communication
                      </a>
                    </li>
                    <li className="list-disc list-inside pl-2 underline cursor-pointer">
                      <a
                        href={`${
                          window.location.origin
                        }/${domain}/communication-envelopes?${buildQSFromQSObj({
                          envelopes__filters_search: user?.afterHoursFilter || '',
                        })}`}
                        className="text-small underline cursor-pointer"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {user.afterHoursCount || 0} After-hours high risk communication{' '}
                      </a>
                    </li>
                  </ul>
                </div>
                {user?.topContacts && user.topContacts.length > 0 && (
                  <div className="flex flex-col gap-1">
                    <span>Top 3 contacts</span>
                    <ul className="flex flex-col gap-1">
                      {user.topContacts?.map((contact) => (
                        <li key={contact.name} className="list-disc list-inside pl-2">
                          {contact.name}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    );

    if (portal) {
      return ReactDOM.createPortal(cardElement, portal);
    }
    return null;
  };

  return <div ref={ref}>{renderProfile()}</div>;
};

export default UserProfile;
